/**
 * SetVendorLoginDialog
 * ─────────────────────────────────────────────────────────────────────────────
 * "Set login": a super-admin sets a vendor's login email and password, then
 * sends the details to the vendor THEMSELVES (Copy, or Open in WhatsApp), as
 * two messages: the sign-in message, whose link fills the email in, then the
 * password on its own so it can be copied exactly (4 Oct 2026).
 * Nothing is sent by Lusuku. This is the fallback for when the emailed
 * set-password link cannot get through; while mail works, Send Login Details
 * is still the better credential, because the vendor chooses the password and
 * the link dies after one use.
 *
 * WHERE THE PASSWORD LIVES
 * ------------------------
 * In this component's state, and nowhere else. It is proposed here
 * (utilities/passwordSuggestion.js, crypto.getRandomValues), the admin may
 * edit it, it is posted once, and the server stores only the hash and never
 * returns it. So the hand-over message is built in the browser from what the
 * admin typed, and closing the dialog throws the password away for good. That
 * is said on screen, so nobody closes it expecting to come back for it.
 *
 * Browsers must not autofill the ADMIN's own saved password into this form,
 * so the password field is a plain text input with autofill off.
 *
 * WHOSE LOGIN
 * -----------
 * The owner's by default; the "Whose login" picker lists the vendor's team
 * members too (super admins only: a shop owner gives their team access
 * through the emailed invite on My Team, never a password). `memberUserId`
 * opens it on a given member, which is how the My Team page uses it while a
 * super admin is signed in as the vendor; `adminSession` then sends both calls
 * as the admin (utilities/impersonation.js), and `onSaved` lets that page
 * refresh the member's row.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { AppButton, AppDialog, AppSelect } from "../ui";
import { apiGetVendorLogin, apiSetVendorLogin } from "../../api/index";
import {
  ADMIN_SET_PASSWORD_MIN_LENGTH,
  adminPasswordProblem,
  suggestVendorPassword,
} from "../../utilities/passwordSuggestion";
import { whatsAppLink } from "../../utilities/phoneNumber";
import { signInLinkFor } from "../../utilities/signInLink";

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const formatWhen = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * A suggestion that already passes the rules for this login. A word can, very
 * rarely, contain the email's name part ("Lion" for lion@shop.ug); drawing
 * again beats showing the admin an error on a password they did not choose.
 * Empty when the browser has no secure randomness: the admin types one.
 */
const suggestionFor = (email) => {
  try {
    let pw = suggestVendorPassword();
    for (let i = 0; i < 5 && adminPasswordProblem(pw, { email }); i += 1) pw = suggestVendorPassword();
    return pw;
  } catch (_err) {
    return "";
  }
};

/**
 * The hand-over is TWO messages. WhatsApp copies a whole message, never one
 * line of it, so neither the email nor a password inside the sign-in message
 * could be copied on its own (Izi, 4 Oct 2026). The first message carries a
 * link that fills the email in (utilities/signInLink); the password follows as
 * a message of its own, which a press and hold copies exactly. No password is
 * ever put in a link.
 *
 * Plain text with no emoji and no dashes, because WhatsApp Desktop mangles
 * astral characters in a wa.me link, and it is read on whatever phone the
 * vendor has.
 */
export const buildLoginHandoverText = ({ greetName, vendorName, loginUrl, loginEmail }) =>
  [
    `Hello ${greetName || vendorName || "there"},`,
    "",
    `Your Lusuku sign-in for ${vendorName} is ready.`,
    "",
    "1. Open this link. Your email is filled in for you:",
    signInLinkFor(loginUrl, loginEmail),
    "",
    "2. Your password comes in the next message. Press and hold it, tap Copy, then paste it into Password.",
    "",
    `Your sign-in email is ${loginEmail}.`,
    "",
    "Once you are in, please change the password: click your name at the top of the left menu, then Change Password.",
    "",
    "Lusuku Support",
  ].join("\n");

/** The second message: the password and nothing else, so it copies exactly. */
export const buildPasswordHandoverText = (password) => String(password || "");

/** Clipboard with a fallback for browsers that refuse the async API. */
const copyText = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_err) {
    /* fall through to the selection copy */
  }
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  } catch (_err) {
    return false;
  }
};

const errorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

/** One message of the hand-over: what to send, and the two ways to send it. */
const HandoverStep = ({ step, title, hint, label, text, mono = false, waHref, waLabel, copyLabel, copied, onCopy }) => (
  <section style={{ marginBottom: 14 }}>
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
      <span
        aria-hidden="true"
        style={{
          flexShrink: 0,
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "#083860",
          color: "#ffffff",
          fontSize: 12,
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 1,
        }}
      >
        {step}
      </span>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{title}</div>
        <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>{hint}</div>
      </div>
    </div>
    <pre
      aria-label={label}
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        padding: "12px 14px",
        fontSize: mono ? 15 : 12.5,
        lineHeight: 1.55,
        letterSpacing: mono ? "0.04em" : undefined,
        color: "#0f172a",
        fontFamily: mono ? "SFMono-Regular, Menlo, Consolas, monospace" : "inherit",
        margin: 0,
        userSelect: "all",
      }}
    >
      {text}
    </pre>
    <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
      {waHref ? (
        <a
          className="lk-action-btn lk-action-btn--lg lk-action-btn--outline"
          style={{ textDecoration: "none" }}
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={waLabel}
        >
          <i className="fab fa-whatsapp" aria-hidden="true" /> Open in WhatsApp
        </a>
      ) : null}
      <AppButton variant="outline" onClick={onCopy} icon={copied ? "fas fa-check" : "fas fa-copy"}>
        {copied ? "Copied" : copyLabel}
      </AppButton>
    </div>
  </section>
);

/**
 * What the dialog calls the business whose login it sets. An event organizer
 * does not think of itself as a vendor; the Cinemax pilot read "vendor" wording
 * as someone else's account (same reason the server's emailAudience exists).
 */
export const partyNounFor = (vendorType) =>
  /ORGANI[SZ]ER/i.test(String(vendorType || "")) ? "organizer" : "vendor";

/** A 401 on an admin-session call: the admin's OWN sign-in lapsed, not the vendor's. */
const ADMIN_SESSION_LAPSED =
  "Your own admin sign-in has expired. Return to admin, sign in again, then try once more.";

/**
 * The people the dialog can set a login for. An older server sends no
 * `people`; the owner is then built from the top-level fields.
 */
const peopleFrom = (data) =>
  Array.isArray(data?.people) && data.people.length
    ? data.people
    : [
        {
          userId: null,
          name: data?.ownerName || data?.vendorName || "Owner",
          isOwner: true,
          roleLabel: "Owner",
          selectable: true,
          loginEmail: data?.loginEmail || null,
          whatsappPhone: data?.whatsappPhone || null,
          signInBlocker: data?.signInBlocker || null,
        },
      ];

const SetVendorLoginDialog = ({
  open,
  onClose,
  vendorType,
  vendorId,
  vendorName,
  memberUserId = null,
  adminSession = false,
  onSaved,
}) => {
  const [phase, setPhase] = useState("loading"); // loading | blocked | form | done
  const [target, setTarget] = useState(null);
  const [people, setPeople] = useState([]);
  const [personKey, setPersonKey] = useState("");
  const [blocked, setBlocked] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(true);
  const [signOutEverywhere, setSignOutEverywhere] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [serverErrors, setServerErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  // Which of the two hand-over messages was copied last: "message" | "password".
  const [copied, setCopied] = useState(null);
  const emailRef = useRef(null);
  const party = partyNounFor(vendorType);

  // Every open starts clean: a password from an earlier vendor must never be
  // sitting in the field when the dialog opens on the next one.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setPhase("loading");
    setTarget(null);
    setPeople([]);
    setPersonKey("");
    setBlocked(null);
    setEmail("");
    setPassword("");
    setShowPassword(true);
    setSignOutEverywhere(false);
    setTouched({ email: false, password: false });
    setServerErrors({});
    setFormError(null);
    setResult(null);
    setCopied(null);

    apiGetVendorLogin(vendorType, vendorId, { adminSession })
      .then(({ data }) => {
        if (!alive) return;
        const list = peopleFrom(data);
        const wanted = memberUserId && list.find((p) => p.userId === String(memberUserId));
        if (memberUserId && (!wanted || wanted.selectable === false)) {
          setBlocked({
            code: "MEMBER_NOT_SELECTABLE",
            message: wanted
              ? `${wanted.name} is ${String(wanted.status || "inactive").toLowerCase()}. Reactivate them on My Team before giving them a login.`
              : `That person is no longer on this ${party}'s team. Reload My Team.`,
          });
          setPhase("blocked");
          return;
        }
        const person = wanted || list[0];
        setTarget(data);
        setPeople(list);
        setPersonKey(person.isOwner ? "owner" : person.userId);
        setEmail(person.loginEmail || "");
        setPassword(suggestionFor(person.loginEmail || ""));
        setPhase("form");
      })
      .catch((err) => {
        if (!alive) return;
        setBlocked({
          code: err?.response?.data?.code || "PREFLIGHT_FAILED",
          message:
            adminSession && err?.response?.status === 401
              ? ADMIN_SESSION_LAPSED
              : errorMessage(err, `Could not load this ${party}'s login.`),
        });
        setPhase("blocked");
      });
    return () => {
      alive = false;
    };
  }, [open, vendorType, vendorId, memberUserId, adminSession, party]);

  const person = useMemo(
    () => people.find((p) => (p.isOwner ? "owner" : p.userId) === personKey) || people[0] || null,
    [people, personKey]
  );

  const pickPerson = useCallback(
    (key) => {
      const next = people.find((p) => (p.isOwner ? "owner" : p.userId) === key);
      if (!next || next.selectable === false) return;
      setPersonKey(key);
      setEmail(next.loginEmail || "");
      setPassword(suggestionFor(next.loginEmail || ""));
      setTouched({ email: false, password: false });
      setServerErrors({});
      setFormError(null);
    },
    [people]
  );

  const minLength = target?.passwordMinLength || ADMIN_SET_PASSWORD_MIN_LENGTH;
  const trimmedEmail = email.trim();
  const emailProblem = !trimmedEmail
    ? `Enter the email the ${party} will sign in with.`
    : !EMAIL_SHAPE.test(trimmedEmail)
      ? "That email address is not valid."
      : null;
  const passwordProblem = adminPasswordProblem(password, { email: trimmedEmail, minLength });
  const emailError = serverErrors.email || (touched.email ? emailProblem : null);
  const passwordError = serverErrors.password || (touched.password ? passwordProblem : null);
  const currentEmail = person?.loginEmail || null;
  const forMember = Boolean(person && !person.isOwner);
  const changingEmail = Boolean(currentEmail) && trimmedEmail.toLowerCase() !== currentEmail.toLowerCase();

  const displayName = target?.vendorName || vendorName || `this ${party}`;

  const handOverText = useMemo(() => {
    if (!result) return "";
    const who = result.accountName || result.ownerName;
    const greetName = who && who !== result.vendorName ? who : result.vendorName;
    const loginUrl =
      result.loginUrl ||
      (typeof window !== "undefined" ? `${window.location.origin}/auth/login-page` : "");
    return buildLoginHandoverText({
      greetName,
      vendorName: result.vendorName || displayName,
      loginUrl,
      loginEmail: result.loginEmail,
    });
  }, [result, displayName]);
  const passwordText = buildPasswordHandoverText(password);

  const waMessageHref = result ? whatsAppLink(result.whatsappPhone || "", handOverText) : null;
  const waPasswordHref = result ? whatsAppLink(result.whatsappPhone || "", passwordText) : null;

  const regenerate = useCallback(() => {
    setPassword(suggestionFor(trimmedEmail));
    setServerErrors((e) => ({ ...e, password: undefined }));
    setShowPassword(true);
  }, [trimmedEmail]);

  const save = useCallback(async () => {
    setTouched({ email: true, password: true });
    setFormError(null);
    setServerErrors({});
    if (emailProblem || passwordProblem) return;
    setSaving(true);
    try {
      const { data } = await apiSetVendorLogin(
        vendorType,
        vendorId,
        {
          email: trimmedEmail,
          password,
          signOutEverywhere,
          memberUserId: forMember ? person.userId : null,
        },
        { adminSession }
      );
      setResult(data);
      setPhase("done");
      toast.success(`Login saved for ${data?.accountName || data?.vendorName || displayName}.`);
      onSaved?.(data);
    } catch (err) {
      const payload = err?.response?.data || {};
      const message =
        adminSession && err?.response?.status === 401
          ? ADMIN_SESSION_LAPSED
          : errorMessage(err, "Could not save the login.");
      if (payload.field === "email" || payload.field === "password") {
        setServerErrors({ [payload.field]: message });
      } else {
        setFormError(message);
      }
    } finally {
      setSaving(false);
    }
  }, [emailProblem, passwordProblem, vendorType, vendorId, trimmedEmail, password, signOutEverywhere, displayName, forMember, person, adminSession, onSaved]);

  const copy = useCallback(async (which, text) => {
    const ok = await copyText(text);
    if (ok) {
      setCopied(which);
      toast.success(
        which === "password"
          ? "Password copied. Send it as a message of its own."
          : "Message copied. Paste it into WhatsApp or SMS."
      );
    } else {
      toast.error("Could not copy. Select the text and copy it by hand.");
    }
  }, []);

  const close = useCallback(() => {
    if (saving) return;
    onClose?.();
  }, [saving, onClose]);

  // ── Footer per phase ──
  let footer = null;
  if (phase === "form") {
    footer = (
      <>
        <AppButton variant="quiet" onClick={close} disabled={saving}>
          Cancel
        </AppButton>
        <AppButton onClick={save} loading={saving} icon="fas fa-lock">
          Save login
        </AppButton>
      </>
    );
  } else if (phase === "done") {
    // Sending happens in the two steps above; the footer only closes.
    footer = <AppButton onClick={close}>Done</AppButton>;
  } else {
    footer = (
      <AppButton variant="quiet" onClick={close}>
        Close
      </AppButton>
    );
  }

  return (
    <AppDialog
      open={open}
      onClose={close}
      busy={saving}
      size="md"
      closeOnBackdrop={phase !== "done"}
      title={phase === "done" ? "Login saved" : "Set login email and password"}
      subtitle={forMember && person ? `${person.name} · ${displayName}` : displayName}
      initialFocusRef={phase === "form" ? emailRef : undefined}
      footer={footer}
    >
      {phase === "loading" && (
        <p style={{ color: "#64748b", margin: "8px 0" }}>Loading this {party}'s login…</p>
      )}

      {phase === "blocked" && blocked && (
        <div className="lk-note lk-note--danger" role="alert">
          <i className="fas fa-exclamation-triangle" aria-hidden="true" style={{ marginTop: 3 }} />
          <span>{blocked.message}</span>
        </div>
      )}

      {phase === "form" && (
        <form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          {people.length > 1 && (
            <div className="lk-field">
              <span className="lk-field__label">Whose login</span>
              <AppSelect
                block
                value={personKey}
                onChange={(e) => pickPerson(e.target.value)}
                options={people.map((p) => ({
                  value: p.isOwner ? "owner" : p.userId,
                  label: `${p.name}${p.roleLabel ? ` (${p.roleLabel})` : ""}`,
                  description:
                    p.selectable === false
                      ? `${String(p.status || "Inactive").toLowerCase()}, reactivate on My Team first`
                      : p.loginEmail || "No usable email yet",
                  disabled: p.selectable === false,
                }))}
              />
            </div>
          )}

          <div className="lk-note lk-note--info" style={{ marginBottom: 16 }}>
            <i className="fas fa-info-circle" aria-hidden="true" style={{ marginTop: 3 }} />
            <span>
              For when email is not getting through. Lusuku sends nothing: after you save, you copy the
              details and send them yourself. They can change the password once they are in.
            </span>
          </div>

          {person?.signInBlocker && (
            <div className="lk-note lk-note--warning" role="status" style={{ marginBottom: 16 }}>
              <i className="fas fa-exclamation-triangle" aria-hidden="true" style={{ marginTop: 3 }} />
              <span>{person.signInBlocker.message}</span>
            </div>
          )}

          <label className="lk-field">
            <span className="lk-field__label">Login email</span>
            <input
              ref={emailRef}
              className={`lk-input${emailError ? " is-invalid" : ""}`}
              type="email"
              name="vendor-login-email"
              autoComplete="off"
              spellCheck={false}
              autoCapitalize="off"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setServerErrors((s) => ({ ...s, email: undefined }));
              }}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
              placeholder="name@business.com"
              aria-invalid={Boolean(emailError)}
            />
            {emailError ? (
              <span className="lk-field__error">{emailError}</span>
            ) : changingEmail ? (
              <span className="lk-field__hint">
                Changes their login from <strong>{currentEmail}</strong>. The old address will no longer sign in.
                {vendorType === "BRAND" && !forMember ? " The brand's email changes with it." : ""}
              </span>
            ) : !currentEmail ? (
              <span className="lk-field__hint">
                {forMember ? "This team member" : `This ${party}`} has no usable login email yet. Enter the one they will use.
              </span>
            ) : (
              <span className="lk-field__hint">{forMember ? "They sign in" : `The ${party} signs in`} with this address.</span>
            )}
          </label>

          <div className="lk-field">
            <label className="lk-field__label" htmlFor={`set-login-password-${vendorId}`}>
              Password
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                id={`set-login-password-${vendorId}`}
                className={`lk-input${passwordError ? " is-invalid" : ""}`}
                type={showPassword ? "text" : "password"}
                name="vendor-handover-secret"
                autoComplete="new-password"
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                data-lpignore="true"
                data-1p-ignore="true"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setServerErrors((s) => ({ ...s, password: undefined }));
                }}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", letterSpacing: "0.02em" }}
                aria-invalid={Boolean(passwordError)}
              />
              <AppButton
                variant="quiet"
                icon={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
              />
              <AppButton
                variant="quiet"
                icon="fas fa-sync-alt"
                onClick={regenerate}
                aria-label="Suggest a new password"
                title="Suggest a new password"
              />
            </div>
            {passwordError ? (
              <span className="lk-field__error">{passwordError}</span>
            ) : (
              <span className="lk-field__hint">
                A suggestion you can keep or change. At least {minLength} characters, with letters and a number.
              </span>
            )}
          </div>

          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", marginTop: 4 }}>
            <input
              type="checkbox"
              checked={signOutEverywhere}
              onChange={(e) => setSignOutEverywhere(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span style={{ fontSize: 13, color: "#334155" }}>
              Also sign them out on every device now
              <span style={{ display: "block", fontSize: 11, color: "#64748b", marginTop: 2 }}>
                Tick this if someone else may know their old password. Leave it off if a shop tablet is signed in
                and taking orders: it would stop until someone signs in again.
              </span>
            </span>
          </label>

          {formError && (
            <div className="lk-note lk-note--danger" role="alert" style={{ marginTop: 14 }}>
              <i className="fas fa-exclamation-triangle" aria-hidden="true" style={{ marginTop: 3 }} />
              <span>{formError}</span>
            </div>
          )}

          {target?.lastSet?.at && (
            <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 14, marginBottom: 0 }}>
              <i className="fas fa-history" aria-hidden="true" /> Login last set {formatWhen(target.lastSet.at)}
              {target.lastSet.byName ? ` by ${target.lastSet.byName}` : ""}.
            </p>
          )}

          {/* Enter in either field submits. */}
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
      )}

      {phase === "done" && result && (
        <>
          <div className="lk-note lk-note--info" style={{ marginBottom: 12 }}>
            <i className="fas fa-check-circle" aria-hidden="true" style={{ marginTop: 3 }} />
            <span>
              Saved. Nothing has been sent: send the two messages below to{" "}
              {result.accountName || "them"} yourself, in this order.
              {result.signedOutEverywhere ? " They have been signed out on every device." : ""}
            </span>
          </div>

          {result.signInBlocker && (
            <div className="lk-note lk-note--warning" role="status" style={{ marginBottom: 12 }}>
              <i className="fas fa-exclamation-triangle" aria-hidden="true" style={{ marginTop: 3 }} />
              <span>{result.signInBlocker.message}</span>
            </div>
          )}

          <HandoverStep
            step={1}
            title="Send the sign-in message"
            hint="Its link fills in their email for them."
            label="Sign-in message to send"
            text={handOverText}
            waHref={waMessageHref}
            waLabel="Open the sign-in message in WhatsApp"
            copyLabel="Copy message"
            copied={copied === "message"}
            onCopy={() => copy("message", handOverText)}
          />
          <HandoverStep
            step={2}
            title="Then send the password on its own"
            hint="As a message of its own it copies exactly: they press and hold it, then tap Copy."
            label="Password to send"
            text={passwordText}
            mono
            waHref={waPasswordHref}
            waLabel="Open the password in WhatsApp"
            copyLabel="Copy password"
            copied={copied === "password"}
            onCopy={() => copy("password", passwordText)}
          />

          <p style={{ fontSize: 11, color: "#64748b", marginTop: 4, marginBottom: 0 }}>
            {result.whatsappPhone
              ? `Open in WhatsApp starts a chat with ${result.whatsappPhone}.`
              : "There is no phone number on file, so WhatsApp will ask you to pick the chat."}{" "}
            The password is only on this screen. Once you close it, it cannot be shown again; set a new one if it
            is lost.
          </p>
        </>
      )}
    </AppDialog>
  );
};

export default SetVendorLoginDialog;
